// Thin lookup that maps a `DungeonTemplateId` back to its template, using
// the Phase 3 starter catalogue for Tier 1-5 and the on-demand high-Tier
// formula from ADR 0027. Kept separate from `dungeon-run` so the latter
// stays free of catalogue imports and is easy to test with synthetic
// templates.
//
// `id` shape: `dungeon.<theme>.t<n>` with `<theme>` ∈ `DUNGEON_THEMES`
// and `<n>` a positive integer. Anything else returns `undefined` —
// callers either reject the run (UI) or treat it the same as "save slot
// references a deleted dungeon" (future content migration).

import {
  DUNGEON_THEMES,
  STARTER_DUNGEON_BY_THEME,
  TUTORIAL_DUNGEON_TEMPLATE,
  createDungeonTemplate,
  type DungeonTheme,
} from '@renavora/content';
import type { DungeonTemplate, DungeonTemplateId } from '@renavora/shared';

const TEMPLATE_ID_RE = /^dungeon\.([a-z][a-z0-9-]*)\.t(\d+)$/;
const THEMES_SET: ReadonlySet<string> = new Set(DUNGEON_THEMES);

function isDungeonTheme(value: string): value is DungeonTheme {
  return THEMES_SET.has(value);
}

/**
 * Resolve a `DungeonTemplateId` to its template. Returns `undefined` for
 * malformed ids, unknown themes, or non-positive Tiers. Starter entries
 * (Tier == theme's starterTier) are read from `STARTER_DUNGEON_BY_THEME`
 * for reference equality with the catalogue array; everything else is
 * generated on demand via `createDungeonTemplate`.
 */
export function resolveDungeonTemplate(id: DungeonTemplateId): DungeonTemplate | undefined {
  // Tier-0 tutorial dungeon (ADR 0038 §2.1) is authored directly, not via the
  // tier ≥ 1 `createDungeonTemplate` formula. Resolve it before the regex/tier
  // guard below (which rejects `t0`).
  if (id === TUTORIAL_DUNGEON_TEMPLATE.id) return TUTORIAL_DUNGEON_TEMPLATE;
  const match = TEMPLATE_ID_RE.exec(id);
  if (match === null) return undefined;
  const [, themePart, tierPart] = match as unknown as [string, string, string];
  if (!isDungeonTheme(themePart)) return undefined;
  const tier = Number(tierPart);
  if (!Number.isInteger(tier) || tier < 1) return undefined;
  const starter = STARTER_DUNGEON_BY_THEME[themePart];
  if (starter.tier === tier) return starter;
  return createDungeonTemplate(themePart, tier);
}
